// Les réglages du bénévolat dans Club › Bénévolat (D-127, maquettes r1 à r7) : les routes déplacées et le résumé de
// chaque rubrique (« Club : table 1, chrono 1… · 3 équipes ont leurs règles », « 2 à ranger »…). Logique pure, testée
// (reglages.test.ts). Tutoiement ; pas d'écriture inclusive.
import type { ParcoursDef } from './badges';

/** Les anciennes pages du bénévolat (vieux liens, favoris, visites d'avant) : leur nouvelle adresse, les réglages du
 *  bénévolat (D-127, une seule page depuis D-139 : #/benevoles/reglages[/<rubrique>]). */
export function movedRoute(hash: string): string | null {
  const h = hash.replace(/\/+$/, '');
  if (h === '#/benevoles/matchs') return '#/benevoles/reglages/besoins';   // l'onglet « Matchs » de D-119
  if (h === '#/benevoles/parcours') return '#/benevoles/reglages/parcours';   // « Parcours et récompenses » de D-100
  if (h === '#/benevoles/recus') return '#/club/registre';             // les reçus à valider (B8 : une route par écran)
  return null;
}

/** Les rubriques des réglages du bénévolat, dans l'ordre de la maquette r1 (la 8e renvoie aux réglages des reçus, D-124). */
export const RUBRICS = ['besoins', 'parcours', 'ranger', 'recompenses', 'jeunes', 'groupes', 'annonces', 'recus'] as const;
export type Rubric = typeof RUBRICS[number];
export const RUBRIC_TITLE: Record<Rubric, string> = {
  besoins: 'Besoins des matchs', parcours: 'Parcours', ranger: 'Ranger les créneaux', recompenses: 'Récompenses des grades',
  jeunes: 'Jeunes', groupes: 'Groupes de bénévoles', annonces: 'Annonces et relances', recus: 'Frais et reçus fiscaux',
};

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
const list = (xs: string[], max = 4) => (xs.length > max ? `${xs.slice(0, max).join(', ')}…` : xs.join(', '));

/** « table », « chrono », « 24 s », « buvette » : un besoin en un mot, pour un résumé. */
export function shortNeed(label: string): string {
  const l = label.trim();
  if (/^tables? de marque$/i.test(l)) return 'table';
  if (/^chrono/i.test(l)) return 'chrono';
  if (/^24 ?(s|sec|secondes)$/i.test(l)) return '24 s';
  return lowerFirst(l);
}

/** Besoins des matchs : « Club : table 1, chrono 1, 24 s 1, buvette 2, arbitre 2 · 3 équipes ont leurs règles ». */
export function needsSummary(roles: { label: string; needed: number }[], unitsWithRules: number): string {
  const club = roles.length ? `Club : ${roles.map(r => `${shortNeed(r.label)} ${r.needed}`).join(', ')}` : 'Les besoins de chaque match à domicile';
  const units = unitsWithRules > 1 ? `${unitsWithRules} équipes ont leurs règles` : unitsWithRules === 1 ? '1 équipe a ses règles' : 'les mêmes pour toutes les équipes';
  return `${club} · ${units}`;
}

/** Parcours : « 9 parcours · 1 créé par le club (Photo et vidéo) · 1 masqué ». */
export function parcoursSummary(defs: ParcoursDef[]): string {
  const created = defs.filter(d => d.custom);
  const hidden = defs.filter(d => d.hidden).length;
  return [
    `${defs.length} parcours`,
    created.length ? `${created.length} ${created.length > 1 ? 'créés' : 'créé'} par le club (${list(created.map(d => d.name), 2)})` : '',
    hidden ? `${hidden} ${hidden > 1 ? 'masqués' : 'masqué'}` : '',
  ].filter(Boolean).join(' · ');
}

/** Ranger les créneaux : la puce « 2 à ranger », ou rien. */
export const toRangeChip = (unknown: number) => (unknown ? `${unknown} à ranger` : '');

/** Récompenses des grades : « Bronze : boisson offerte · Argent : gourde du club · Or : place au premier rang du gala ». */
export function rewardsSummary(rewards: { grade: string; title: string; perk: boolean }[]): string {
  const name: Record<string, string> = { herbe: 'En herbe', bronze: 'Bronze', argent: 'Argent', or: 'Or', legende: 'Légende' };
  const order = ['bronze', 'argent', 'or', 'legende', 'herbe'];
  const gifts = rewards.filter(r => r.perk).sort((a, b) => order.indexOf(a.grade) - order.indexOf(b.grade)).slice(0, 3);
  return gifts.length ? gifts.map(r => `${name[r.grade] ?? r.grade} : ${lowerFirst(r.title)}`).join(' · ') : 'Un merci dans l’app à chaque grade';
}

/** Jeunes : « Ouverts aux jeunes par défaut : buvette, gâteaux, installation, accueil ». */
export function youthSummary(labels: { label: string; open: boolean; club: boolean; auto: boolean }[]): string {
  const on = labels.filter(l => l.club && l.open).map(l => lowerFirst(l.label));
  if (on.length) return `Ouverts aux jeunes par défaut : ${list(on)}`;
  return labels.some(l => !l.club && l.auto) ? 'Par défaut : l’arbitrage et la table des matchs de jeunes' : 'Aucun créneau ouvert aux jeunes par défaut';
}

/** Groupes de bénévoles : « Création ouverte · essai 30 jours · une mission tous les 60 jours ». */
export const groupsSummary = (s: { group_create_open: boolean; group_trial_days: number; group_every_days: number }) =>
  `${s.group_create_open ? 'Création ouverte' : 'Créés par le bureau'} · essai ${s.group_trial_days} jours · une mission tous les ${s.group_every_days} jours`;

/** Annonces et relances : « « Bravo du lundi » · Assiduité · relance la veille à 18 h ». */
export function relancesSummary(s: { badges_bravo: boolean; badges_assiduite: boolean; volunteer_reminder_hour: number | null }): string {
  const parts = [s.badges_bravo ? '« Bravo du lundi »' : '', s.badges_assiduite ? 'Assiduité' : '',
    s.volunteer_reminder_hour != null ? `relance la veille à ${s.volunteer_reminder_hour} h` : 'pas de relance la veille'];
  return parts.filter(Boolean).join(' · ');
}

/** Les délais proposés (les réglages du club acceptent 7 à 90 jours d'essai, 14 à 180 jours de rythme). */
export const TRIAL_CHOICES = [15, 21, 30, 45, 60];
export const EVERY_CHOICES = [30, 45, 60, 90, 120];
export const REMINDER_CHOICES = [17, 18, 19, 20];
